import { SoheilStage } from "./SoheilStage";
import type { Pose } from "@soheil/core";
import type { TimeOfDay } from "@soheil/core";
import { toFa } from "@soheil/core";

interface Props {
  pose: Pose;
  time: TimeOfDay;
  /** آیتم‌های باز شدهٔ اتاق — از core/room */
  unlocked: string[];
  /** متن ساعت روی میز؛ همان عددی که کاربر روی صفحه می‌بیند */
  clock: string;
}

const SKY: Record<TimeOfDay, [string, string]> = {
  morning: ["#EBE0CE", "#E3D5C0"],
  evening: ["#E8D3BC", "#D9BFA6"],
  night: ["#3B3145", "#4A3B4E"],
};

const PANE: Record<TimeOfDay, string> = {
  morning: "#BBD3E0",
  evening: "#E2B183",
  night: "#1B1526",
};

/**
 * اتاق سهیل. سه چیز اینجا اتفاق می‌افتد:
 *   ۱. ساعت روی میز دقیقاً همان تایمر کاربر است — مهم‌ترین جزئیات محصول
 *   ۲. آیتم‌ها با سطح اتاق ظاهر می‌شوند و هرگز پس گرفته نمی‌شوند
 *   ۳. نور بین صبح، عصر و شب عوض می‌شود
 */
export function Room({ pose, time, unlocked, clock }: Props) {
  const has = (id: string) => unlocked.includes(id);
  const night = time === "night";
  const [c1, c2] = SKY[time];

  return (
    <div className="room" data-time={time} data-pose={pose}>
      <svg viewBox="0 0 320 236" className="room-back" aria-label="اتاق مطالعهٔ سهیل">
        <defs>
          <linearGradient id="sky" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={c1} />
            <stop offset="100%" stopColor={c2} />
          </linearGradient>
        </defs>

        <rect width="320" height="236" fill="url(#sky)" />

        {/* پنجره — ستارهٔ سهیل فقط شب دیده می‌شود */}
        <rect x="200" y="22" width="94" height="72" rx="8" fill={PANE[time]} />
        {night && (
          <g>
            <circle cx="219" cy="42" r="1.5" fill="#EFE7DC" opacity=".7" />
            <circle cx="251" cy="36" r="1.1" fill="#EFE7DC" opacity=".5" />
            <circle cx="279" cy="56" r="1.3" fill="#EFE7DC" opacity=".6" />
            <path d="M265 42l2 4.6 5 .4-3.8 3.3 1.2 4.9L265 52.5 260.6 55l1.2-4.9-3.8-3.3 5-.4L265 42z" fill="#D9963A" />
          </g>
        )}
        <rect x="200" y="22" width="94" height="72" rx="8" fill="none" stroke="#6E4F37" strokeWidth="4" />
        <path d="M247 24v68M202 58h90" stroke="#6E4F37" strokeWidth="3" />

        {has("shelf") && (
          <g>
            <rect x="24" y="50" width="66" height="5" rx="2.5" fill="#6E4F37" />
            <rect x="32" y="32" width="9" height="18" rx="2" fill="#7BA893" />
            <rect x="43" y="28" width="8" height="22" rx="2" fill="#D9963A" />
            <rect x="53" y="34" width="9" height="16" rx="2" fill="#9B8AAE" />
          </g>
        )}

        {has("poster") && (
          <g>
            <rect x="26" y="76" width="40" height="30" rx="4" fill="#2C242F" opacity={night ? ".55" : ".28"} />
            <path d="M46 82l2.4 5.4 5.9.5-4.5 3.9 1.4 5.7L46 94.5 40.8 97.5l1.4-5.7-4.5-3.9 5.9-.5L46 82z" fill="#D9963A" />
          </g>
        )}

      </svg>

      {/* شخصیت لایهٔ میانی است: جلوی دیوار، پشت میز. مختصاتش در styles.css
          طوری تنظیم شده که دقیقاً جای قبلیِ رگ (x=78 y=-6 scale=0.30) بنشیند. */}
      <SoheilStage pose={pose} />

      <svg viewBox="0 0 320 236" className="room-front" aria-hidden="true">
        <defs>
          <radialGradient id="lampGrad" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#F5C070" stopOpacity=".55" />
            <stop offset="45%" stopColor="#D9963A" stopOpacity=".22" />
            <stop offset="100%" stopColor="#D9963A" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* میز جلوی سهیل کشیده می‌شود تا پشت‌میز نشستنش باورپذیر باشد */}
        <rect x="14" y="196" width="292" height="11" rx="4" fill="#8B6647" />
        <rect x="14" y="196" width="292" height="3" fill="#A07A57" />
        <rect x={has("chair") ? "30" : "34"} y="207" width="10" height="29" fill="#6E4F37" />
        <rect x="276" y="207" width="10" height="29" fill="#6E4F37" />
        {has("chair") && <rect x="24" y="180" width="8" height="28" rx="3" fill="#7A5940" />}

        {has("lamp") && (
          <>
            <g style={{ opacity: night ? 1 : 0.25 }}>
              <ellipse cx="60" cy="172" rx="86" ry="62" fill="url(#lampGrad)" className="lampglow" />
            </g>
            <g>
              <path d="M60 196v-30" stroke="#6E4F37" strokeWidth="4" />
              <path d="M60 166l-17 3 12-22 15 3z" fill="#D9963A" />
              <path d="M43 169h34" stroke="#F2D9AE" strokeWidth="2.5" strokeLinecap="round" />
              <rect x="47" y="191" width="26" height="6" rx="3" fill="#6E4F37" />
            </g>
          </>
        )}

        {/* ساعت میز = تایمر اپ */}
        <g>
          <rect x="236" y="167" width="54" height="30" rx="6" fill="#FAF6EE" />
          <rect x="236" y="167" width="54" height="30" rx="6" fill="none" stroke="#B0742A" strokeWidth="2" />
          <text x="263" y="188" textAnchor="middle" fontFamily="Lalezar, serif" fontSize="17" fill="#2D2A32">
            {toFa(clock)}
          </text>
        </g>

        {has("plant") && (
          <g>
            <path d="M304 232v-16" stroke="#5C7A63" strokeWidth="3" />
            <ellipse cx="297" cy="210" rx="10" ry="6" fill="#7BA893" transform="rotate(-24 297 210)" />
            <ellipse cx="311" cy="206" rx="10" ry="6" fill="#8FBBA4" transform="rotate(22 311 206)" />
            <path d="M296 226h17l-2 10h-13z" fill="#B0742A" />
          </g>
        )}
      </svg>
    </div>
  );
}
